import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { Gift, Moon, Sun } from "lucide-react";
import { motion } from 'framer-motion';
import { MAIN_BG_GRADIENT } from '../utils/constants';

const GoogleIcon = (
  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [referralCode, setReferralCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDark, setIsDark] = useState(false);

  const handleGoogleClick = async () => {
    setIsSubmitting(true);
    try {
      const userCredential = await loginWithGoogle();
      const user = userCredential.user;
      await userService.registerUser(user, referralCode);
      navigate("/beta-access");
    } catch (error) {
      console.error("Error during authentication:", error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const glow = document.getElementById("mouse-glow");
    const update = (e) => {
      glow.style.transform = `translate(${e.clientX - 150}px, ${e.clientY - 150}px)`;
    };
    document.addEventListener("mousemove", update);
    return () => document.removeEventListener("mousemove", update);
  }, []);

  return (
    <div className={`${isDark ? 'dark' : ''} relative min-h-screen transition-colors duration-300 ${MAIN_BG_GRADIENT}`}>
      {/* <div className="absolute top-5 right-5 z-50">
        <button
          onClick={() => setIsDark(!isDark)}
          className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-md"
        >
          {isDark ? <Sun className="text-yellow-400 w-5 h-5" /> : <Moon className="text-gray-600 w-5 h-5" />}
        </button>
      </div> */}

      <div className="flex flex-col min-h-screen overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div id="mouse-glow" className="absolute w-72 h-72 bg-white/30 rounded-full blur-3xl -z-10 transition-transform"></div>
        </div>
        <main className="flex-1 flex items-center justify-center py-24 px-4 z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="bg-white/60 dark:bg-white/10 rounded-3xl shadow-2xl p-10 max-w-md w-full backdrop-blur-md border border-gray-200 dark:border-gray-700"
          >
            <div className="text-center mb-10">
              <motion.img
                src="/logo.png"
                alt="Quis Lab Logo"
                className="mx-auto h-16 w-16 animate-[wiggle_1.5s_ease-in-out_infinite] mb-2"
              />
              <h1 className="text-5xl font-extrabold text-gray-800 dark:text-white tracking-tight">Quis</h1>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="text-gray-600 dark:text-gray-300 mt-3 text-sm"
              >
                Sign in to your account or create a new one
              </motion.p>
            </div>

            <Tabs defaultValue="signup" className="w-full">
              <TabsContent value="signup">
                <form className="space-y-6">
                  <div>
                    <label htmlFor="referral-code" className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      Referral Code <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <div className="relative mt-1">
                      <Gift className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        id="referral-code"
                        type="text"
                        placeholder="Enter referral code"
                        className="pl-10 w-full py-2 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <button
                      onClick={handleGoogleClick}
                      disabled={isSubmitting}
                      className="w-full flex items-center justify-center border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-md shadow-sm transition-all"
                    >
                      {GoogleIcon}
                      Continue With Google
                    </button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </motion.div>
        </main>
        <footer className="text-center py-6 text-sm text-gray-400 dark:text-gray-500 z-10">
          <p>&copy; 2025 Quis Lab. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
